import { Box, Typography, Stack, Divider, Chip, Avatar, Button, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import defaultImage from 'assets/images/default-user-image.png';
import { getPersonAvatar } from 'apis/controllers/person';
import { useTheme } from '@emotion/react';
import { CalendarToday, CalendarTodayOutlined, Home, MailOutline, Phone } from '@mui/icons-material';

const PersonInfo = ({ person, hasAccess }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [avatar, setAvatar] = useState('');
    const theme = useTheme();

    useEffect(() => {
        const fetch = async () => {
            const result = await getPersonAvatar({ personId: person.id });
            setAvatar(result);
        };

        fetch();
    }, [person.id]);

    return (
        <Paper
            elevation={3}
            sx={{
                my: 3,
                mb: 2,
                p: 2.5,
                borderRadius: 3,
                transition: '0.3s ease',
                '&:hover': {
                    boxShadow: 6
                },
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2
            }}
        >
            {avatar ? (
                <img
                    src={avatar === '' ? defaultImage : avatar}
                    alt="Avatar"
                    style={{ width: 140, marginRight: 12, borderRadius: '20%' }}
                />
            ) : (
                <Avatar src={avatar === '' ? defaultImage : avatar} alt="No " sx={{ width: 100, height: 100 }} />
            )}
            <Box style={{ width: '100%' }}>
                <Box style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Stack direction="row" alignItems="center" divider={<Divider orientation="vertical" flexItem />} spacing={1}>
                        <>
                            <Typography sx={{ fontWeight: 500 }} variant="h4">{`${person.firstName} ${person.lastName}`}</Typography>
                            {person.fathersName && (
                                <Typography variant="body2" color="text.secondary">
                                    {`(ΠΑΤΡΩΝΥΜΟ: ${person.fathersName.toUpperCase()})`}
                                </Typography>
                            )}
                        </>
                        <Typography variant="body1">{person.gender.description}</Typography>
                    </Stack>
                    <div>
                        <Chip label={person.status?.description} sx={{ background: person.status?.color1, color: '#fefefe' }} />
                    </div>
                </Box>
                {person.dob && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <CalendarTodayOutlined fontSize="small" />
                        <div>Ημ/νια Γεν: {format(new Date(person.dob), 'dd/MM/yyyy')}</div>
                    </Stack>
                )}

                {person.eMail && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <MailOutline fontSize="small" />
                        <a href={`mailto:${person.eMail}`} style={{ color: 'blue' }}>
                            {person.eMail}
                        </a>
                    </Stack>
                )}
                {(person.phone1 || person.phone2) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Phone fontSize="small" />
                        <div>{person.phone1 && person.phone2 ? `${person.phone1} - ${person.phone2}` : person.phone1 || person.phone2}</div>
                    </Stack>
                )}

                {/* Address */}
                {(person.street || person.streetNr || person.zipCode || person.city) && (
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Home fontSize="small" />
                        <div>{[person.address1, person.streetNr, person.zipCode, person.city].filter((v) => !!v).join(' ')}</div>
                    </Stack>
                )}

                {hasAccess && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {person.hasUser ? (
                            <Button
                                sx={{ padding: 1, marginTop: 1 }}
                                variant="contained"
                                onClick={() => navigate(`/users-and-access/users/manage/${person?.userId}/details`)}
                            >
                                {t('ViewUser')}
                            </Button>
                        ) : (
                            <Button
                                sx={{ padding: 1, marginTop: 1 }}
                                variant="contained"
                                onClick={() => navigate(`/users-and-access/users/create/${person?.id}`)}
                            >
                                {t('CreateNewUser')}
                            </Button>
                        )}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

PersonInfo.propTypes = {
    person: PropTypes.object
};

export default PersonInfo;
